import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  Optional,
  SkipSelf,
  ChangeDetectorRef,
  isDevMode
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common'; // Added for reusable template rendering
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { RouterLink, Router, NavigationEnd, RouterLinkActive } from '@angular/router';
import { FeatureToggleDirective } from '@shared/directives/feature-toggle.directive';
import { CheckOpenDirective } from '@shared/layout/directives/check-open.directive';
import { MenuItem } from '@shared/layout/services/menu-items';
import { AuthStore } from "@features/auth/auth.store";
import { AuthSessionService } from "@features/auth/auth-session.service";
import { HasPermissionDirective } from "@features/auth/has-permission.directive";
import { MatIconModule} from "@angular/material/icon";

@Component({
  selector: 'pd-nav',
  imports: [
    NgTemplateOutlet,
    MatListModule,
    RouterLink,
    MatButtonModule,
    MatExpansionModule,
    CheckOpenDirective,
    FeatureToggleDirective,
    RouterLinkActive,
    HasPermissionDirective,
    MatIconModule
  ],
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavComponent {
  protected authStore = inject(AuthStore);
  private authSession = inject(AuthSessionService);
  protected isCorporate = this.authStore.isCorporate;
  protected readonly isDevMode = isDevMode;

  @Input() menuItems: MenuItem[] = [];

  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  currentUrl: string = this.router.url;

  constructor(
    @Optional() @SkipSelf() private checkOpenDirective: CheckOpenDirective,
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Evaluates feature flag exceptions based on user roles and state
   */
  shouldShowItem(item: MenuItem): boolean {
    if (item.featureFlagName === 'identityVerification') {
      return !this.isCorporate();
    }
    if (item.featureFlagName === 'withdraw') {
      return !this.isCorporate() || this.isDevMode();
    }
    return true;
  }

  /**
   * Matches original layout logic where specific feature flags bypassed structural permission checks
   */
  bypassPermissionCheck(item: MenuItem): boolean {
    return item.featureFlagName === 'identityVerification' || item.featureFlagName === 'withdraw';
  }

  isMenuItemActive(item: MenuItem): boolean {
    const url = this.currentUrl;
    if (!item.route) return false;

    if (item.route.startsWith('/transaction-details')) {
      return url.startsWith('/transaction-details');
    }
    if (item.name === 'Balance History') {
      return url.endsWith('balance') || url === '/balance-history' || url.endsWith('/request-details');
    }
    if (item.name === 'Crypto Transaction') {
      return url.endsWith('crypto') || url === '/crypto-transactions';
    }
    return url === item.route;
  }

  logout(route: string | null) {
    if (route === null) {
      this.authSession.logout();
    }
  }

  isActiveChange() {
    if (this.checkOpenDirective) {
      this.checkOpenDirective.isOpen = true;
      this.checkOpenDirective.isActiveChange();
    }
  }
}
